/**
 * Browser writer-correlation id minting (PR #620 r6, C1).
 *
 * The SDK used to generate ONE session id in its constructor and reuse it for
 * every session the page load started — the first chat, an "Ask another
 * question" restart, a post-onboarding reconnect. It is now a narrow writer
 * fence for Conversation batches/finalize calls. Pipecat independently mints
 * the server run id used for DemoSession, recordings, and billing.
 *
 * The fix is mint-at-START: every session START draws a fresh id from this
 * source. The constructor's id is not wasted — `current()` exposes it so
 * pre-start consumers (`getSessionId()`, log lines) name the session that is
 * ABOUT to start, and the first `begin()` hands that same id out, so nothing
 * observed before the first start is contradicted after it.
 *
 * Conversation continuity is deliberately NOT on this id: it rides the
 * conversationId + visitor token (FLO-524/544). A fresh session id per start
 * changes which writer a conversation row points at — which is the point:
 * `Conversation.pipecatSessionId` is the current browser-started writer, not
 * an end-user identity or server run id.
 *
 * Pure and SDK-free so the vitest `node` env can exercise it for real; the
 * call-site wiring in onboarding-sdk.ts is pinned by source assertions
 * (tests/session-id.test.ts), same approach as consent-mode.test.ts.
 */

/** A cryptographically random browser writer-correlation id. */
export function generateSessionId(): string {
  // crypto.randomUUID() is available in all modern browsers and Node 14.17+.
  return `session_${crypto.randomUUID()}`;
}

/** One id per session START; a stable read in between. */
export class SessionIdSource {
  private id = generateSessionId();
  private consumed = false;

  /**
   * The id of the most recently begun session — or, before any session has
   * begun, the id the FIRST one will use.
   */
  current(): string {
    return this.id;
  }

  /**
   * Mint the writer id for a session starting RIGHT NOW. The
   * first call returns the constructor-era id (so pre-start reads stay
   * truthful); every later call mints a fresh one, so two sequential sessions
   * in one page load cannot share a Conversation writer fence.
   */
  begin(): string {
    if (this.consumed) {
      this.id = generateSessionId();
    }
    this.consumed = true;
    return this.id;
  }
}
